import { useState, useEffect, useCallback } from 'react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Warning, MagnifyingGlass, ArrowClockwise, Calendar } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

export default function FailedBookingsLogPage() {
  const { user } = useAuth();
  const isCA = user?.role === 'accountant';
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [agency, setAgency] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (agency) params.set('agency', agency);
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    try {
      const r = await api.get(`/failed-bookings/?${params.toString()}`);
      setRows(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, [q, agency, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const exportExcel = () => {
    if (rows.length === 0) { toast.error('No rows to export'); return; }
    const headers = isCA
      ? ['Attempted', 'Consumer ID', 'Customer', 'Mobile', 'Agency', 'Amount', 'Card', 'Sub-Staff', 'Reason', 'Tries']
      : ['Attempted', 'Consumer ID', 'Customer', 'Mobile', 'Agency', 'Amount', 'Account Email', 'Password', 'Card', 'Sub-Staff', 'Reason', 'Tries'];
    const rowData = rows.map(r => isCA ? [
      r.last_attempted_at ? new Date(r.last_attempted_at).toLocaleString('en-IN') : '',
      r.consumer_id, r.customer_name, r.mobile, r.agency_name,
      r.amount, r.card_last4 ? `****${r.card_last4}` : '',
      r.sub_staff_name || r.parent_staff_name || '', r.reason || '', r.attempt_count,
    ] : [
      r.last_attempted_at ? new Date(r.last_attempted_at).toLocaleString('en-IN') : '',
      r.consumer_id, r.customer_name, r.mobile, r.agency_name,
      r.amount, r.account_email, r.account_password,
      r.card_last4 ? `****${r.card_last4}` : '',
      r.sub_staff_name || r.parent_staff_name || '', r.reason || '', r.attempt_count,
    ]);
    let html = '<html><head><meta charset="utf-8"></head><body><table border="1">';
    html += '<tr style="background:#c00;color:#fff;font-weight:bold">' +
      headers.map(h => `<th>${h}</th>`).join('') + '</tr>';
    rowData.forEach(row => {
      html += '<tr style="background:#FFC7CE">' +
        row.map(c => `<td>${c ?? ''}</td>`).join('') + '</tr>';
    });
    html += '</table></body></html>';
    const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const suffix = agency ? `_${agency.replace(/\s/g, '_')}` : '';
    a.download = `FailedBookings${suffix}_${Date.now()}.xls`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} entries`);
  };

  const updateReason = async (id, current) => {
    const n = window.prompt('Update failure reason:', current || '');
    if (n === null) return;
    try {
      await api.patch(`/failed-bookings/${id}/reason`, { reason: n });
      toast.success('Reason updated');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="failed-bookings-log">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Warning size={26} weight="duotone" className="text-red-600" /> Failed Bookings Log
        </h1>
        <span className="text-sm text-gray-500">Permanent audit — complaint resolution ke liye</span>
        <Button variant="outline" size="sm" className="ml-auto" onClick={load} data-testid="refresh-btn">
          <ArrowClockwise size={14} /> Refresh
        </Button>
        <Button size="sm" onClick={exportExcel} className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-excel-btn">
          📥 Export Excel
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded p-3 flex flex-wrap items-end gap-2" data-testid="filter-bar">
        <div className="flex-1 min-w-[200px]">
          <div className="text-[10px] text-gray-500 mb-0.5">Search</div>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-2 text-gray-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Consumer ID / Mobile / Name" className="pl-7 h-8 text-xs" data-testid="search-input" />
          </div>
        </div>
        <div className="min-w-[160px]">
          <div className="text-[10px] text-gray-500 mb-0.5">Agency</div>
          <Input value={agency} onChange={(e) => setAgency(e.target.value)} placeholder="Agency name contains…" className="h-8 text-xs" data-testid="agency-input" />
        </div>
        <div className="min-w-[140px]">
          <div className="text-[10px] text-gray-500 mb-0.5"><Calendar size={10} className="inline" /> From</div>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-8 text-xs" />
        </div>
        <div className="min-w-[140px]">
          <div className="text-[10px] text-gray-500 mb-0.5">To</div>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-8 text-xs" />
        </div>
        <Button size="sm" className="h-8" onClick={load} data-testid="apply-filter-btn">Apply</Button>
      </div>

      {/* Results */}
      <div className="text-xs text-gray-600">{loading ? 'Loading…' : `${rows.length} failed entries`}</div>
      <div className="overflow-x-auto bg-white border rounded">
        <table className="w-full text-xs">
          <thead className="bg-red-50 text-[10px] uppercase text-red-900">
            <tr>
              <th className="px-2 py-2 text-left">Attempted</th>
              <th className="px-2 py-2 text-left">Consumer ID</th>
              <th className="px-2 py-2 text-left">Customer</th>
              <th className="px-2 py-2 text-left">Agency</th>
              <th className="px-2 py-2 text-right">Amount</th>
              {!isCA && <th className="px-2 py-2 text-left">Account Email</th>}
              {!isCA && <th className="px-2 py-2 text-left">Password</th>}
              <th className="px-2 py-2 text-left">Card</th>
              <th className="px-2 py-2 text-left">Sub-Staff</th>
              <th className="px-2 py-2 text-left">Reason</th>
              <th className="px-2 py-2 text-center">Tries</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={isCA ? 9 : 11} className="text-center py-8 text-gray-400">{loading ? 'Loading…' : 'Koi failed booking nahi mili'}</td></tr>
            ) : rows.map(r => {
              const d = r.last_attempted_at ? new Date(r.last_attempted_at).toLocaleString('en-IN') : '-';
              return (
                <tr key={r.id} className="border-t hover:bg-red-50/30" data-testid={`failed-row-${r.id}`}>
                  <td className="px-2 py-1">{d}</td>
                  <td className="px-2 py-1 font-mono">{r.consumer_id}</td>
                  <td className="px-2 py-1">{r.customer_name || '-'}<div className="text-[10px] text-gray-500">{r.mobile}</div></td>
                  <td className="px-2 py-1">{r.agency_name || '-'}</td>
                  <td className="px-2 py-1 text-right">{fmt(r.amount)}</td>
                  {!isCA && <td className="px-2 py-1 font-mono text-[11px]">{r.account_email || '-'}</td>}
                  {!isCA && <td className="px-2 py-1 font-mono text-[11px]">{r.account_password || '-'}</td>}
                  <td className="px-2 py-1 font-mono">{r.card_last4 ? `****${r.card_last4}` : '-'}</td>
                  <td className="px-2 py-1">{r.sub_staff_name || r.parent_staff_name || '-'}</td>
                  <td className="px-2 py-1">
                    <button onClick={() => updateReason(r.id, r.reason)} className="text-left hover:bg-blue-50 rounded px-1" title="Click to edit">
                      {r.reason || <span className="text-gray-400 italic">(click to add)</span>}
                    </button>
                  </td>
                  <td className="px-2 py-1 text-center"><span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-700 font-bold text-[10px]">{r.attempt_count}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {isCA && (
        <div className="text-[10px] text-gray-500 bg-gray-50 border border-gray-200 rounded p-2">
          ℹ️ CA role: Account email and password fields are hidden for compliance. Contact admin for full details.
        </div>
      )}
    </div>
  );
}
